import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { fetchCampaignAtom } from '@/store';

export const useRefetchCampaign = (campaignSlug: string) => {
  const queryClient = useQueryClient();
  const refetchCampaign = useAtomValue(fetchCampaignAtom);

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['campaign', campaignSlug] });

    if (refetchCampaign) {
      await refetchCampaign();
    }
  }, [queryClient, campaignSlug, refetchCampaign]);

  return refetch;
};

export const useRefetchCampaigns = () => {
  const queryClient = useQueryClient();
  const delayedRefetchTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Invalidating the ['campaigns', …] query triggers React Query to refetch; the
  // _authenticated sync effect (keyed on the query data) then repopulates the atoms
  // consumers still read. Awaiting the invalidation resolves once the refetch completes.
  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
  }, [queryClient]);

  /**
   * Refetch immediately, then schedule follow-up refetches to pick up
   * async updates (e.g., AI-generated campaign names from Celery tasks).
   */
  const refetchWithDelayedUpdates = useCallback(async () => {
    // Clear any pending timers from a previous call
    delayedRefetchTimers.current.forEach(clearTimeout);
    delayedRefetchTimers.current = [];

    await refetch();

    // Schedule follow-up refetches to catch async name generation
    const delays = [3000, 6000, 12000];
    for (const delay of delays) {
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      }, delay);
      delayedRefetchTimers.current.push(timer);
    }
  }, [refetch, queryClient]);

  // Clean up pending timers on unmount
  useEffect(() => {
    return () => {
      delayedRefetchTimers.current.forEach(clearTimeout);
    };
  }, []);

  return { refetch, refetchWithDelayedUpdates };
};
